/// <reference types="google.maps" />
import { useEffect, useState } from 'react';
import { api, type GoogleMapsSettings } from './api';

export type MapsAvailability =
  | { state: 'loading' }
  | { state: 'unavailable' }
  | { state: 'ready'; settings: Extract<GoogleMapsSettings, { enabled: true }> };

export function useGoogleMapsSettings(userId: number | null): MapsAvailability {
  const [availability, setAvailability] = useState<MapsAvailability>({ state: 'loading' });
  useEffect(() => {
    let cancelled = false;
    setAvailability({ state: 'loading' });
    api.mapsSettings(userId).then(
      (settings) =>
        !cancelled &&
        setAvailability(settings.enabled ? { state: 'ready', settings } : { state: 'unavailable' }),
      () => !cancelled && setAvailability({ state: 'unavailable' }),
    );
    return () => {
      cancelled = true;
    };
  }, [userId]);
  return availability;
}

declare global {
  interface Window {
    __registryMapsReady?: () => void;
    gm_authFailure?: () => void;
  }
}

let loading: Promise<typeof google.maps> | undefined;
let loadedKey: string | undefined;
export const MAPS_FAILURE_EVENT = 'registry-google-maps-error';

/** One script across form/map mounts, including React Strict Mode. */
export function loadGoogleMaps(browserKey: string): Promise<typeof google.maps> {
  if (loadedKey && loadedKey !== browserKey) {
    return Promise.reject(new Error('Reload the page to use the updated map settings.'));
  }
  if (loading) return loading;
  loadedKey = browserKey;
  loading = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const clear = () => {
      window.clearTimeout(timeout);
      delete window.__registryMapsReady;
    };
    const fail = () => {
      clear();
      script.remove();
      loading = undefined;
      loadedKey = undefined;
      reject(new Error('Google Maps could not load.'));
    };
    const timeout = window.setTimeout(fail, 20_000);
    window.__registryMapsReady = () => {
      clear();
      resolve(google.maps);
    };
    window.gm_authFailure = () => {
      window.dispatchEvent(new Event(MAPS_FAILURE_EVENT));
      fail();
    };
    script.onerror = fail;
    script.async = true;
    script.referrerPolicy = 'strict-origin-when-cross-origin';
    const params = new URLSearchParams({
      key: browserKey,
      callback: '__registryMapsReady',
      loading: 'async',
      v: 'quarterly',
      auth_referrer_policy: 'origin',
      language: 'en',
      region: 'US',
    });
    script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
    document.head.append(script);
  });
  return loading;
}
